import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { emitRefresh } from '../utils/refresh';

const NavIcon = ({ path, active }) => {
  const icons = {
    '/': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.2 : 1.8} strokeLinecap="round">
        <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
      </svg>
    ),
    '/schedule': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.2 : 1.8} strokeLinecap="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
        {active && <path d="M8 14h2M12 14h2M8 18h2"/>}
      </svg>
    ),
    '/book': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.2 : 1.8} strokeLinecap="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18M9 16l2 2 4-4"/>
      </svg>
    ),
    '/updates': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.2 : 1.8} strokeLinecap="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
    ),
    '/account': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.2 : 1.8} strokeLinecap="round">
        <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
      </svg>
    ),
  };
  return icons[path] || null;
};

// Profile completeness — keep this field list in sync with the copy in Account.js
// Session V (W2): demographics are NOT part of completeness. Height, weight and
// race are POPIA consent-gated and genuinely optional — race at least offers
// "Prefer not to say", height and weight have no opt-out at all — so requiring
// them left a permanent nag badge on the Account tab until staff disclosed
// their body measurements and race. A badge that only clears on disclosure is
// pressure, and consent under pressure is not consent.
//
// Session AA: t-shirt size and waist size ADDED. This is not a reversal of the
// above — those two are ORDINARY personal information with no consent gate, so
// there is no consent for a badge to put under pressure. They sit on the same
// footing as the photo: an agency operational need (a uniform cannot be ordered
// without them) rather than something the staff member is being asked to
// disclose about themselves.
// ⚠️ `gender` is deliberately NOT here. It has no stated operational purpose
// yet — it was collected because the intake form asked for it. Requiring a
// field nobody can justify is the weakest possible position under POPIA
// minimisation. Revisit only if a real use appears.
//
// Completeness is: the photo, the five bank fields (without them the person
// cannot be paid), and the two uniform sizes.
const isProfileComplete = p => !!(
  p && p.picture &&
  p.bank_name && p.account_holder && p.account_number && p.branch_code && p.account_type &&
  p.tshirt_size && p.pants_size
);

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [newConfirmed, setNewConfirmed] = useState(0);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  // Last pair of counts seen, so a RISE can be told from a fall. A ref, not
  // state: it must not cause a render, and the interval callback needs the
  // latest value rather than the one captured when the interval was set.
  const prevCounts = useRef(null);

  // This 30s poll was already here and already detected an acceptance — it lit
  // a badge and stopped, leaving the screen behind it wrong. It now also tells
  // the open screen to refetch, which costs NO extra requests.
  //
  // ⚠️ Promise.all rather than two loose .then()s, so both numbers are compared
  // against the previous pair at the same instant. Two independent callbacks
  // emit twice when both counters move together, which is exactly what an
  // acceptance does: it writes an update row AND an accepted application.
  const fetchCounts = () => {
    Promise.all([
      axios.get('/api/staff/unread-count').then(r => r.data.count).catch(() => null),
      axios.get('/api/staff/schedule-unseen-count').then(r => r.data.count).catch(() => null),
    ]).then(([u, s]) => {
      if (u !== null) setUnread(u);
      if (s !== null) setNewConfirmed(s);

      const prev = prevCounts.current;
      // ⚠️ Emit on an INCREASE only, never on any change. Schedule.js posts
      // /api/staff/schedule-seen inside its own fetch, so a refresh drives
      // schedule-unseen straight back to 0 — on "any change" that 1 → 0 would
      // emit a second, pointless refresh every single time.
      if (prev && ((u !== null && u > prev.unread) || (s !== null && s > prev.confirmed))) {
        emitRefresh();
      }
      // ⚠️ The first run SEEDS without emitting. Otherwise anyone who opens the
      // app holding one unread update goes 0 → 1 on mount, and every screen
      // fetches twice before they have touched anything.
      // A failed request (null) carries the previous value forward rather than
      // counting as a drop to zero, which would fake a rise on the next poll.
      prevCounts.current = {
        unread: u !== null ? u : (prev ? prev.unread : 0),
        confirmed: s !== null ? s : (prev ? prev.confirmed : 0),
      };
    });
  };

  // Completeness only changes via user action on the Account page, so fetch on
  // mount + route change only — NOT on the 30s interval (keeps polling load flat).
  const fetchProfileState = () => {
    axios.get('/api/staff/me')
      .then(r => setProfileIncomplete(!isProfileComplete(r.data)))
      .catch(() => setProfileIncomplete(false));
  };

  useEffect(() => {
    fetchCounts();
    const t = setInterval(fetchCounts, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    fetchCounts();
    fetchProfileState();
  }, [location.pathname]);

  const tabs = [
    { path: '/', label: 'Next Shift' },
    { path: '/schedule', label: 'Schedule' },
    { path: '/book', label: 'Book Shift' },
    { path: '/updates', label: 'Updates' },
    { path: '/account', label: 'Account' },
  ];

  return (
    <nav className="bottom-nav">
      {tabs.map(tab => {
        const active = location.pathname === tab.path;
        return (
          <button key={tab.path} className={`nav-item ${active ? 'active' : ''}`}
            onClick={() => {
              // ⚠️ Tapping the tab you are ALREADY on used to be a genuine
              // no-op: navigate() to the current path does not remount the
              // route, so the screen's mount effect never re-ran. It is also
              // the first thing people try when a screen looks stale.
              // force=true so two quick taps both work — the throttle in
              // useAutoRefresh is for automatic triggers, not deliberate ones.
              if (active) { emitRefresh(true); fetchCounts(); fetchProfileState(); }
              else navigate(tab.path);
            }}>
            <NavIcon path={tab.path} active={active} />
            {tab.path === '/updates' && unread > 0 && (
              <span className="nav-badge">{unread > 9 ? '9+' : unread}</span>
            )}
            {tab.path === '/schedule' && newConfirmed > 0 && (
              <span className="nav-badge">{newConfirmed > 9 ? '9+' : newConfirmed}</span>
            )}
            {tab.path === '/account' && profileIncomplete && (
              <span className="nav-badge" style={{width:8,height:8,minWidth:8,padding:0,borderRadius:'50%',fontSize:0,lineHeight:0}} />
            )}
            {tab.label}
          </button>
        );
      })}
    </nav>
  );
}
